import { PARTY_DETAILS_GRID_KEY } from '../../../container/forms/schemas/GiftHospitalityDonation/constants';
import {
  applyPartyDetailRowsToFormData,
  hasPartyDetailGrid,
  readPartyDetailRows,
} from '../../../container/forms/schemas/GiftHospitalityDonation/partyDetails';
import { applyDefaultGivenReceivedByPerson } from '../../../container/forms/schemas/GiftHospitalityDonation/givenReceivedByPerson';
import { FormState } from '../../../container/forms/types/core/form-state.types';
import { toMailString, toMailTagOptions } from '../../../../utils/transformers';
import { requiresGiftHospitalityAuthoriser } from '../../../container/forms/schemas/GiftHospitalityDonation/authoriserRules';
import { toCamelCaseKeys, toSnakeCaseKeys } from './utils';

type GeneratedSectionSubmitPayload = {
  fields: Record<string, unknown>;
  gridData: Record<string, unknown>;
  documents: Record<string, unknown>;
};

export interface GiftHospitalityDonationApiSection {
  fields: Record<string, unknown>;
  gridData?: unknown;
  documents?: unknown;
}

const PARTY_DETAILS_API_KEY = 'party_details';

const REQUEST_DETAILS_FIELDS = [
  'registerType',
  'givenReceived',
  'date',
  'givenReceivedByPerson',
  'pitchorTenderInProgress',
  'instruction',
  'discountOffered',
  'discountValue',
  'chairtyName',
  'description',
  'reason',
  'reasonTooltip',
  'estimatedValue',
  'estimatedValueTooltip',
  'donationApproval',
  'confirmInstruction',
  'authoriser',
  'authoriserInstruction',
] as const;

const APPROVAL_FIELDS = [
  'approval_approve',
  'approval_rejection_details',
  'approval_comments',
  'gift_hospitality_donation_dubai_approval_approve',
  'gift_hospitality_donation_dubai_approval_rejection_details',
  'gift_hospitality_donation_dubai_approval_comments',
  'gift_hospitality_donation_dublin_approval_approve',
  'gift_hospitality_donation_dublin_approval_rejection_details',
  'gift_hospitality_donation_dublin_approval_comments',
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isRequestDetailsSection = (sectionKey: string): boolean =>
  sectionKey.endsWith('_request_details');

const fieldsForSection = (sectionKey: string): readonly string[] => {
  if (isRequestDetailsSection(sectionKey)) {
    return REQUEST_DETAILS_FIELDS;
  }

  if (sectionKey.endsWith('_approval')) {
    return APPROVAL_FIELDS;
  }

  return [];
};

const pickDefinedValues = (
  data: FormState['data'],
  names: readonly string[]
): Record<string, unknown> =>
  names.reduce<Record<string, unknown>>((values, name) => {
    if (data[name] !== undefined) {
      values[name] = data[name];
    }
    return values;
  }, {});

const applyMailField = (
  fields: Record<string, unknown>,
  name: string
): void => {
  if (!(name in fields)) {
    return;
  }

  const mail = toMailString(fields[name]);
  if (mail) {
    fields[name] = mail;
    return;
  }

  delete fields[name];
};

const toSnakeCaseRows = (rows: unknown): unknown => {
  if (!Array.isArray(rows)) {
    return rows;
  }

  return rows.map((row) => (isRecord(row) ? toSnakeCaseKeys(row) : row));
};

const toCamelCaseRows = (rows: unknown): unknown => {
  if (!Array.isArray(rows)) {
    return rows;
  }

  return rows.map((row) => (isRecord(row) ? toCamelCaseKeys(row) : row));
};

const readPartyRows = (gridData: unknown): unknown => {
  if (!isRecord(gridData)) {
    return undefined;
  }

  return (
    gridData[PARTY_DETAILS_API_KEY] ??
    gridData.given_or_received_by ??
    gridData[PARTY_DETAILS_GRID_KEY]
  );
};

/**
 * UK, Dubai, and Dublin share these fields. Only the section key changes by office.
 */
export const mapGiftHospitalityDonationSectionToFormState = (
  section: GiftHospitalityDonationApiSection,
  formState: FormState
): void => {
  Object.assign(formState.data, toCamelCaseKeys(section.fields ?? {}));

  if (formState.data.givenReceivedByPerson !== undefined) {
    const personTags = toMailTagOptions(formState.data.givenReceivedByPerson);
    formState.data.givenReceivedByPerson =
      personTags.length > 0 ? personTags : undefined;
  }
  if (formState.data.authoriser !== undefined) {
    formState.data.authoriser = toMailTagOptions(formState.data.authoriser);
  }
  applyDefaultGivenReceivedByPerson(
    formState.data as Record<string, unknown>
  );
  if (isRecord(section.documents)) {
    Object.assign(formState.data, toCamelCaseKeys(section.documents));
  }

  const savedRows = readPartyRows(section.gridData);
  if (savedRows === undefined) {
    return;
  }

  applyPartyDetailRowsToFormData(
    formState.data as Record<string, unknown>,
    toCamelCaseRows(savedRows),
    formState.data.givenOrReceivedBy
  );
};

export const buildGiftHospitalityDonationSectionSubmitPayload = (
  sectionKey: string,
  data: FormState['data']
): GeneratedSectionSubmitPayload => {
  const fields = pickDefinedValues(data, fieldsForSection(sectionKey));

  applyMailField(fields, 'givenReceivedByPerson');

  if ('authoriser' in fields && !requiresGiftHospitalityAuthoriser(data)) {
    delete fields.authoriser;
  } else {
    applyMailField(fields, 'authoriser');
  }

  const partyData = data as Record<string, unknown>;
  const rows = hasPartyDetailGrid(partyData)
    ? readPartyDetailRows(partyData)
    : undefined;

  return {
    fields: toSnakeCaseKeys(fields),
    documents: {},
    gridData:
      isRequestDetailsSection(sectionKey) && rows !== undefined
        ? { [PARTY_DETAILS_API_KEY]: toSnakeCaseRows(rows) }
        : {},
  };
};
